import { GoogleLoginInput } from 'dtos/users/GoogleLoginInput';
import { LoginOutput } from 'dtos/users/LoginOutput';
import { emailAlreadyExistsMessage } from 'dtos/users/common';
import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { LoadStatus } from 'utils/types';

export class SocialAuthStore {
  status: LoadStatus | 'errorEmailExists' = 'none';
  message?: string;

  constructor(private readonly authStore: AuthStore) {
    makeAutoObservable(this, {}, { autoBind: true });

    window.addEventListener('message', this.handleReceiveMessage);
  }

  reset() {
    this.status = 'none';
    this.message = undefined;
  }

  async handleGoogleAuthResponse(credentialResponse: any) {
    this.status = 'loading';
    try {
      const dto: GoogleLoginInput = {
        token: credentialResponse.credential,
      };

      const response = await fetch(`${serverUri}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });
      if (!response.ok) {
        const json = nullsToUndefined(await response.json());
        runInAction(() => {
          if (json && json.message === emailAlreadyExistsMessage) {
            this.status = 'errorEmailExists';
            this.message = 'Account already exists. Did you sign up with an email and password?';
          } else {
            this.status = 'error';
            this.message = `Unable to log in.${json.message ? ` ${json.message}` : ''} Please try again.`;
          }
        });
        return;
      }
      const output: LoginOutput = nullsToUndefined(await response.json());
      runInAction(() => {
        this.status = 'success';
      });
      this.authStore.handleLoginResponse(output);
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = `Unable to log in.${err instanceof Error ? ` ${err.message}` : ''} Please try again.`;
      });
    }
  }

  azureLogin(state?: string) {
    // state can be read in event.data.state below
    const url = new URL(`${serverUri}/auth/azure`);
    if (state) {
      url.searchParams.append('state', state);
    }
    const target = 'azureLogin';
    const width = 483;
    const height = 600;
    const top = window.outerHeight / 2 + window.screenY - height / 2;
    const left = window.outerWidth / 2 + window.screenX - width / 2;
    window.open(url, target, `scrollbars=yes,width=${width},height=${height},top=${top},left=${left}`);
  }

  handleReceiveMessage = async (event: MessageEvent) => {
    if (event.origin !== serverUri) {
      return;
    }
    const { type, access_token, user, error, message } = event.data;
    if (type === 'azureLogin') {
      if (error) {
        if (message === emailAlreadyExistsMessage) {
          this.status = 'errorEmailExists';
          this.message = 'Account already exists. Did you sign up with an email and password?';
        } else {
          this.status = 'error';
          this.message = `Unable to log in.${message ? ` ${message}` : ''} Please try again.`;
        }
      } else {
        this.status = 'success';
        this.authStore.handleLoginResponse({ access_token, user });
      }
    }
  };
}
